import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useHistory, useParams } from "react-router";

import {
    Grid,
    Typography,
    IconButton,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';

import {
    ExitConfirmationModal,
    BreadCrumbs,
    LoadingBar,
    Stepper
} from '../../Common';
import { trakrefFontFamily, trakrefFontFamilyBold } from '../../../utils';
import { trakrefBlue } from '../../../color_utils';
import Routes from '../../../routes';

import Assets from './auditassets';
import Agents from './auditagents';
import Information from './auditinformation';
import useFormDirty from '../../../hooks/useFormDirty';

const determineSuccessText = (history) => (
    <span style={{
        fontSize: '14px',
        color: '#FFFFFF',
    }}>
        A new audit has been successfully created.
        <span style={{ fontWeight: '100' }}>
            The audit can be viewed in  <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => history.push('/Reports/Store Location Site Audit')}>Reports</span>.
        </span>
    </span>
);

const formatValue = (val) => {
    if (val && val.length > 0 && val[0] === "+") {
        const i = val.indexOf(" ");
        return val.substring(val.length + 1, i).trimStart();
    }
    return val;
};

const initialAssetState = {
    assetId: "",
    isOpen: true,
    hasError: false,
};

const initialAgentState = {
    name: "",
    city: "",
    agency: "",
    state: "",
    badgeNumber: "",
    country: "",
    contactNumber: "",
    countryCode: "+1",
    emailAddress: "",
    address: "",
    isOpen: true,
    nameHasError: false,
    phoneHasError: false,
    emailHasError: false,
}

const initialInfoState = {
    staff: "",
    infoShared: "",
    notes: "",
    duration: "",
    auditDate: "",
    staffHasError: false,
    auditDateHasError: false
}

const CreateAudit = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [exitModal, setExitModal] = useState(false);

    const [activeStep, setActiveStep] = useState(0);
    const [assetOptions, setAssetOptions] = useState([]);
    const [agentOptions, setAgentOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);

    const [assets, setAssets] = useState([{
        id: crypto.randomUUID(),
        ...initialAssetState
    }]);
    const [agents, setAgents] = useState([{
        id: crypto.randomUUID(),
        ...initialAgentState,
    }]);
    const [info, setInfo] = useState(initialInfoState);

    const history = useHistory();
    const { initializeData, isDirty } = useFormDirty();
    const { id: locationId } = useParams();

    const ClosePage = () => {
        history.push(`/Location/${locationId}`);
    };

    const validateAssetForm = () => {

        const failedAssets = assets.filter(x => !x.assetId || x.hasError);

        if (failedAssets.length > 0) {
            const newAssets = assets.map(asset => ({
                ...asset,
                hasError: asset.assetId ? false : true
            }));
            setAssets(newAssets);
            return false;
        }

        return true;
    };

    const validateAgentForm = () => {
        const failedAgents = agents.filter(x => !x.name || x.emailHasError || x.phoneHasError);

        if (failedAgents.length > 0) {
            const newAgents = agents.map(agent => ({
                ...agent,
                nameHasError: agent.name ? false : true
            }));
            setAgents(newAgents);
            return false;
        }

        return true;
    };

    const validateInforForm = () => {

        let isValid = true;

        if (!info.staff) {
            setInfo(prev => ({
                ...prev,
                staffHasError: true
            }));

            isValid = false;
        }

        if (!info.auditDate) {
            setInfo(prev => ({
                ...prev,
                auditDateHasError: true
            }));

            isValid = false;
        }

        return isValid;
    };

    const validateForm = () => {

        if (activeStep === 0 && !validateAssetForm()) {
            return false;
        }

        if (activeStep === 1 && !validateAgentForm()) {
            return false;
        }

        if (activeStep === 2 && !validateInforForm()) {
            return false;
        }

        return true;
    };

    const onHandleNextStep = () => {
        if (!validateForm() || activeStep === 2)
            return;

        setActiveStep(prev => prev += 1);
    };

    const onHandleSwitchStep = (step) => {

        if (step < activeStep) {
            setActiveStep(step);
            return;
        }

        if (!validateForm())
            return;

        setActiveStep(prev => step > prev ? prev += 1 : step);
    };

    const submit = async () => {
        try {
            setSaving(true);

            if (!validateInforForm()) {
                setSaving(false);
                return;
            }

            const body = {
                locationId,
                ownerId: info.staff,
                duration: info.duration,
                informationShared: info.infoShared,
                auditDate: info.auditDate,
                notes: info.notes,
                locationAuditAssets: assets.map(x => ({ assetId: x.assetId })),
                locationAuditAgents: agents.map(x => ({
                    name: x.name,
                    city: x.city,
                    agency: x.agency,
                    state: x.state,
                    badgeNumber: x.badgeNumber,
                    country: x.country,
                    phone: x.contactNumber && `${x.countryCode} ${formatValue(x.contactNumber)}`,
                    email: x.emailAddress,
                    address: x.address,
                }))
            }

            const response = await fetch(Routes.Location.AddLocationAudit(), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            })

            const newdata = await response.json();
            setSaving(false);

            if (response.status === 200) {
                ClosePage();
                toast.success(determineSuccessText(history), { duration: 6000 });
                /* setAlert({
                    render: true,
                    text: determineSuccessText(history),
                    type: 'success'
                });
                setTimeout(() => closeAndResetAlert(), 5000); */
                return;
            }

            toast.error('There was an error saving the location audit', { duration: 6000 });
            if (newdata.length > 0) {
                newdata.forEach(i => toast.error(i.exceptionMessage, { duration: 6000 }));
            }
        }
        catch (error) {
            console.log(error);
            toast.error('There was an error saving the location audit', { duration: 6000 });
            setSaving(false);
        }
        finally {
            setSaving(false);
        }
    }

    const fetchOptionData = useCallback(async () => {
        if (!locationId)
            return;

        const response = await fetch(Routes.Location.GetLocationAuditDropdowns(locationId));
        const data = await response.json();

        setCountryOptions(data.country);
        setStateOptions(data.state);
        setAssetOptions(data.assets);
        setAgentOptions(data.staff);
        setLoading(false);
    }, [locationId]);

    useEffect(() => {
        fetchOptionData();
    }, [fetchOptionData]);

    useEffect(() => {
        const assetsWithoutId = assets.map(({ id, isOpen, hasError, ...rest }) => (rest));
        const agentsWithoutId = agents.map(({ id, isOpen, nameHasError, phoneHasError, emailHasError, ...rest }) => (rest));
        initializeData({
            assetsWithoutId,
            agentsWithoutId,
            info
        });
    }, [])

    const CTA = () => activeStep === 2 ? submit() : onHandleNextStep();
    const CTAlabel = activeStep === 2 ? "Create Audit" : "Next";

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: 'Locations', to: '/Locations' },
                    { text: `Location #${locationId}`, to: `/Location/${locationId}` },
                    { text: `Create Audit` }
                ]}
            />

            <Grid
                container
                direction="row"
                alignItems="center"
                style={{ maxWidth: '65%' }}
            >
                <Grid item>
                    <IconButton aria-label="close"
                        onClick={() => {
                            const assetsWithoutId = assets.map(({ id, isOpen, hasError, ...rest }) => (rest));
                            const agentsWithoutId = agents.map(({ id, isOpen, nameHasError, phoneHasError, emailHasError, ...rest }) => (rest));
                            if (isDirty({ assetsWithoutId, agentsWithoutId, info })) {
                                setExitModal(true);

                                return;
                            };

                            ClosePage();
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item>
                    <Typography
                        variant="h5"
                        
                        style={{ color: '#1A173B' }}
                    >
                        Create Audit
                    </Typography>
                </Grid>
                <Grid item align="right" sx={{ marginLeft: 'auto' }}>
                    <LoadingButton
                        variant="contained"
                        loading={saving}
                        onClick={CTA}
                        sx={{
                            "& .MuiCircularProgress-root": {
                                color: "#FFFFFF",
                                width: "21px !important",
                                height: "21px !important",
                            },
                            "&.MuiButton-root": {
                                fontFamily: trakrefFontFamilyBold,
                                backgroundColor: trakrefBlue,
                                letterSpacing: "1.2px",
                            },
                        }}
                    >
                        <span style={{ color: saving ? "transparent" : "#fff" }}>
                            {CTAlabel}
                        </span>
                    </LoadingButton>
                </Grid>
            </Grid >

            <Grid container mb={4} ml={3} sx={{ maxWidth: "65%" }} spacing={2}>
                <Grid item xs={12} sx={{ marginTop: "7px" }}>
                    <Stepper
                        activeStep={activeStep}
                        steps={[
                            'Assets',
                            'Agents',
                            'Audit Information',
                        ]}
                        onStepChange={onHandleSwitchStep}
                    />
                    {
                        activeStep === 0 && (
                            <Assets
                                initialState={initialAssetState}
                                assets={assets}
                                options={assetOptions}
                                setAssets={setAssets}
                            />
                        )
                    }
                    {
                        activeStep === 1 && (
                            <Agents
                                initialState={initialAgentState}
                                agents={agents}
                                options={agentOptions}
                                setAgents={setAgents}
                                countries={countryOptions}
                                states={stateOptions}
                            />
                        )
                    }
                    {
                        activeStep === 2 && (
                            <Information
                                options={agentOptions}
                                info={info}
                                setInfo={setInfo}
                            />
                        )
                    }
                </Grid>
            </Grid>

            <ExitConfirmationModal
                open={exitModal}
                close={() => setExitModal(false)}
                leaveOnClick={ClosePage}
                stayOnClick={() => setExitModal(false)}
            />
        </>
    );
};

export default CreateAudit;